// ============================================================================
// Authentication Types
// ============================================================================

export interface LoginRequest {
  email: string;
  password: string;
}

export interface OAuthLoginPayload {
  email: string;
  password: string;
  twofa_code?: string;
  skip_2fa?: boolean;
  google_secret?: string;
}

// API Response from POST /oauth_login
export interface OAuthLoginApiResponse {
  authToken: string;
  token?: string; // Alternative field name for authToken (used in Google login)
  role: string;
  type?: string; // Alternative field name for role (used in Google login)
  username: string;
  institution_id: number;
  user_id: number;
  is_2FA: boolean;
  is_2FA_setup: boolean;
  secret_2FA?: string;
  detail: string;
}

// API Response from GET /auth_me
export interface AuthMeApiResponse {
  id: number;
  user_id?: number; // May be included in some responses
  name: string;
  email: string;
  heb_name?: string;
  type: string;
  role: string;
  institution_id: number;
  active: boolean;
  clinic_admin: number;
  sub_clinic: string;
  sub_clinic_list: string[];
  clinicservices_id: number[];
  show_approve_treatment: boolean;
  show_prep_meeting: boolean;
  show_clicks_tasks: boolean;
  has_alerts: boolean;
  new_alerts: number;
  created_at: number;
  updated_at: number | null;
  data?: string;
  phone_ext?: string;
  ext_id?: string;
  last_login?: number | null;
  lang?: string;
  manage_users?: boolean;
  manage_clalit_excel?: boolean;
  whatsapp?: boolean;
  mobile_tracking?: boolean;
  prompt_developer?: boolean;
  hide_update_prescription?: boolean;
  ai_support?: boolean;
  ai_support_admin?: boolean;
  // 2FA fields
  is_2FA?: boolean;
  is_2FA_setup?: boolean;
  secret_2FA?: string;
  // Token fields (not in auth_me, but may be in other responses)
  authToken?: string;
  token?: string;
}

// Unified login response used throughout the app
export interface LoginResponse {
  authToken: string;
  user: User;
}

export interface GoogleAuthRequest {
  google_code?: string;
  is_google_code?: boolean;
  redirect_uri?: string;
  twofa_code?: string;
  google_secret?: string;
  skip_2fa?: boolean;
}

// ============================================================================
// User Types
// ============================================================================

export interface User {
  id: number | string;
  user_id?: number;
  email: string;
  name?: string;
  type?: string;
  role: string;
  institution_id?: number;
  active?: boolean;
  clinic_admin?: number;
  sub_clinic?: string;
  sub_clinic_list?: string[];
  clinicservices_id?: number[];
  show_approve_treatment?: boolean;
  show_prep_meeting?: boolean;
  show_clicks_tasks?: boolean;
  has_alerts?: boolean;
  new_alerts?: number;
  is_2FA?: boolean;
  is_2FA_setup?: boolean;
  secret_2FA?: string;
  phone_ext?: string;
  manage_users?: boolean;
  manage_clalit_excel?: boolean;
  whatsapp?: boolean;
  mobile_tracking?: boolean;
  hide_update_prescription?: boolean;
  prompt_developer?: boolean;
  /** May open the AI Support assistant. */
  ai_support?: boolean;
  /** Human support: sees every AI Support session, approves actions, publishes KB entries. */
  ai_support_admin?: boolean;
}

// ============================================================================
// Clinic Types
// ============================================================================

// API Response from GET /clinics (array of clinic objects)
export interface ClinicApiResponse {
  id: number;
  name: string;
  created_at?: number;
  institution_id: number;
  active?: boolean;
  clinic_admin?: number;
  sub_clinic?: string;
  show_approve_treatment?: boolean;
  show_prep_meeting?: boolean;
  show_clicks_tasks?: boolean;
  last_idx?: number;
  id_prefix?: string;
  lang?: string;
  timezone?: string;
  google_doc_folder?: string;
  prompt_prefix?: string;
  sparrow_questionannaire_id?: string;
  meeting_cc_list?: string[];
  send_doctor_email_when_summary_ready?: boolean;
  intake?: boolean;
  demo?: boolean;
  add_quetionnaire_text_to_adtl_input?: boolean;
  triage?: boolean;
  default_case_manager_id?: number;
  min_age?: number;
  default_funding_entity?: string;
  need_approval_for_treatment?: boolean;
  appointment_link?: string;
  logo?: string;
  footer_contact_time?: string;
  footer_line1?: string;
  footer_line2?: string;
  heb_name?: string;
  email_template?: string;
  SMS_sender?: string;
  email_name?: string;
  email_sender?: string;
  address?: string;
  phone?: string;
  email?: string;
}

// Simplified clinic type used in User object
export interface Clinic {
  id: number;
  name: string;
  created_at?: number;
  institution_id?: number;
  last_idx?: number;
  id_prefix?: string;
  lang?: string;
  timezone?: string;
  google_doc_folder?: string;
  prompt_prefix?: string;
  sparrow_questionannaire_id?: string;
  meeting_cc_list?: string[];
  send_doctor_email_when_summary_ready?: boolean;
  intake?: boolean;
  demo?: boolean;
  add_quetionnaire_text_to_adtl_input?: boolean;
  triage?: boolean;
  default_case_manager_id?: number;
  min_age?: number;
  default_funding_entity?: string;
  need_approval_for_treatment?: boolean;
  appointment_link?: string;
  logo?: string;
  footer_contact_time?: string;
  footer_line1?: string;
  footer_line2?: string;
  heb_name?: string;
  email_template?: string;
  SMS_sender?: string;
  email_name?: string;
  email_sender?: string;
  address?: string;
  phone?: string;
  email?: string;
}

export interface GetClinicsResponse {
  items: ClinicApiResponse[];
  total_count?: number;
}

// ============================================================================
// Patient Types
// ============================================================================

// Task object within patient records
export interface PatientTask {
  issue: string;
  issue_severity: string;
  issue_type: string;
  issue_owner: number;
  issue_date: number;
}

// ============================================================================
// Task Types (for /task_types endpoint)
// ============================================================================

export interface TaskType {
  id: number;
  created_at: number;
  type: string;
  default_assign: number[];
  assign_type: string;
  meeting_drop_down?: boolean;
  default_action?: string;
}

export interface CreateInternalTaskPayload {
  token: string;
  patient_id: number;
  type: string;
  assign_to: number[];
  priority: string;
  status: string;
  description: string;
  meeting_id?: number;
}

export interface CreateInternalTaskResponse {
  id: number;
  created_at: number;
  patient_id: number;
  type: string;
  created_by: number;
  assign_to: number[];
  priority: string;
  status: string;
  description: string;
  clinic_id: number;
  HM_ID: string;
  first_name: string;
  last_name: string;
}

export interface InternalTaskItem {
  id: number;
  created_at: number;
  last_update: number | null;
  patient_id: number;
  type: string;
  created_by: number;
  assign_to: number[];
  priority: string;
  status: string;
  description: string;
  clinic_id: number;
  HM_ID: string;
  first_name: string;
  last_name: string;
  meeting_id?: number;
  case_manager_id?: number;
  doctor_id?: number;
  /** Assigned therapist, else the preferred one - what a new meeting defaults to.
   *  The Xano get_tasks rows carry no prefered_* fields, so these currently equal
   *  the assigned ids. */
  default_doctor_id?: number;
  default_case_manager_id?: number;
}

export interface GetInternalTasksPayload {
  token: string;
  patient_id: number;
  user_id: number;
  clinic_id?: number;
}

export interface GetInternalTaskByIdPayload {
  token: string;
  id: number;
  patient_id: number;
}

export type GetInternalTasksResponse = InternalTaskItem[];

// ============================================================================
// Case Closure (Finish Treatment) Approval
// ============================================================================

// GET /finish_approval_data?token={token}&patient_id={id}
export interface FinishApprovalData {
  name: string;                           // patient full name
  patient_tz: string;                     // Id (TZ)
  HM_ID: string;                          // HM_ID
  finish_reason: string;                  // patient.cancel_reason
  finish_notes: string;                   // approval notes if saved, else cancel_notes
  request_by: number | null;              // finish_request_user (resolve to name via users list)
  requested_date: number | null;          // finish_request_date (ms epoch → DD/MM/YYYY)
  cm_summary: string;                     // case-manager intake summary_text (read-only)
  finish_request_approval_notes: string;  // prefill for the Psychiatric Closure Notes box
  is_formal: boolean;                      // backend-computed: Maccabi AND originating status "pending"
}

// POST /finish_approval
export interface FinishApprovalPayload {
  token: string;
  patient_id: number;
  save_only: boolean;   // true = only persist closure_notes; no status change
  is_formal: boolean;   // true = also record patient_contacts + (later) email patient
  finish_reason: string;   // pass through from the loaded data
  closure_notes: string;   // current content of the Psychiatric Closure Notes box
}

export interface FinishApprovalResponse {
  status: "saved" | "approved";
}

export interface UpdateInternalTaskPayload {
  token: string;
  id: number;
  patient_id: number;
  type: string;
  assign_to: number[];
  priority: string;
  status: string;
  description: string;
  meeting_id?: number;
}

export interface DeleteInternalTaskPayload {
  token: string;
  id: number;
}

export interface UpdateInternalTaskResponse {
  id: number;
  created_at: number;
  last_update: number;
  patient_id: number;
  type: string;
  created_by: number;
  assign_to: number[];
  priority: string;
  status: string;
  description: string;
  clinic_id: number;
  HM_ID: string;
  first_name: string;
  last_name: string;
  meeting_id?: number;
}

// ============================================================================
// Notes Types
// ============================================================================

export interface Note {
  id: number;
  created_at: number;
  created_by: number;
  object: string;
  object_id: number;
  text: string;
}

export interface GetNotesPayload {
  token: string;
  object: string;
  object_id: number;
}

export type GetNotesResponse = Note[];

export interface CreateNotePayload {
  token: string;
  object: string;
  object_id: number;
  text: string;
}

export interface CreateNoteResponse {
  id: number;
  created_at: number;
  created_by: number;
  object: string;
  object_id: number;
  text: string;
}

export interface UpdateNotePayload {
  token: string;
  id: number;
  object: string;
  object_id: number;
  text: string;
}

export interface UpdateNoteResponse {
  id: number;
  created_at: number;
  created_by: number;
  object: string;
  object_id: number;
  text: string;
}

// ============================================================================
// Task History Types
// ============================================================================

export interface TaskHistoryItem {
  id: number;
  created_at: number;
  patient_id: number;
  date: number;
  meeting_id: number;
  task_id: number;
  user_id: number;
  action: string;
  type: string;
  notes: string;
}

export interface GetTaskHistoryPayload {
  token: string;
  task_id: number;
}

export type GetTaskHistoryResponse = TaskHistoryItem[];


// ============================================================================
// Tasks Types (for /patients_tasks endpoint)
// ============================================================================

// Individual task item from GET /patients_tasks
export interface PatientTaskItem {
  // Task specific fields
  issue: string;
  issue_owner: number;
  issue_date: number;
  issue_meeting_id: number;
  issue_meetingtype_id: number;
  issue_severity: string;
  issue_type: string;
  id: number;

  // Patient identification
  patient_id: string;
  patient_name: string;
  first_name: string;
  last_name: string;
  HM_ID: string;
  status: string;
  is_test: boolean;
  age: string;

  // Clinic and assignment
  clinic_id: number;
  sub_clinic: string;
  doctor_id: number;
  case_manager_id: number;
  /** Assigned therapist, else the preferred one - what a new meeting defaults to. */
  default_doctor_id: number;
  default_case_manager_id: number;
  patient_type: string;

  // Notes
  task: string;

  // Meeting information
  next_meeting_start: number;
  next_meeting: number;
  next_meeting_team_url: string;
  next_meeting_yoman_url: string;
  next_meeting_type_id: number;

  // Contact information
  email: string;
  phone: string;
  main_phone: string;
  gender: string;

  // Insurance and funding
  HMO: string;

  // Family information
  children: number;
  parents_marital_status: string;
  parent_1_name: string;
  parent_1_gender: string;
  parent_1_age: number;
  parent_1_id: string;
  parent_1_email: string;
  parent_1_phone_number: string;
  parent_2_name: string;
  parent_2_gender: string;
  parent_2_age: number;
  parent_2_id: string;
  parent_2_email: string;
  parent_2_phone_number: string;

  // Status and location
  overide_status: string;
  city: string;
  area: string;
  updated_at: number;

  // Contact information
  contact_name: string;
  contact_phone: string;
  contact_role: string;

  // Cancellation
  cancel_reason: string;
  cancel_notes: string;

  // Obligations and verification
  obligation_intake: string;
  obligation_followup: string;
  obligation_verified: boolean;
  followup_obligation_verified: boolean;

  // Meeting counts and budgets (JSON strings)
  meeting_count: string;
  meeting_budget: string;
  meeting_count_text1: string;
  meeting_count_text2: string;
  meeting_count_text3: string;
  meeting_count_text4: string;
  meeting_count_total_text: string;

  // Warnings and other info
  set_meeting_warnings: string[];
  move_clinic_comments: string;
  parent_2_sign_link: string;
  // "email" or "parent_1_email" - which address belongs on this patient. See
  // patient_utils.own_email_field; the Edit Email modal labels and writes this one.
  own_email_field: string;
  zoho_contact_id: string;
}

// Request payload for GET /patients_tasks
export interface GetPatientsTasksPayload {
  token: string;
  clinic_id: string | number;
  user_id?: number;
}

// Response from GET /patients_tasks
export type GetPatientsTasksResponse = PatientTaskItem[];

// API Response for individual patient items
export interface PatientIntakeItem {
  id: number;
  HM_ID: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  gender: string;
  DOB: string;
  status: string;
  active: boolean;
  age: string;
  patient_id: string;
  city: string;
  source: string;
  funding_entity: string;
  communication_status: string;
  created_at: number;
  updated_at: number;
  clinic_id: number;

  // Meeting and appointment fields
  doctor_id: number;
  case_manager_id: number;
  default_doctor_id: number;
  default_case_manager_id: number;
  next_meeting: number;
  next_meeting_start: number | null;
  next_meeting_type: string;
  next_meeting_type_id: number;
  last_meeting: number | null;
  last_interaction: number | null;

  // Status and finish fields
  overide_status: string;
  cancel_reason: string;
  cancel_notes: string;
  invitation_status: string;

  // Contact information
  contact_name: string;
  contact_phone: string;
  contact_role: string;
  area: string;

  // Meeting count fields
  meeting_budget: string; // JSON string
  meeting_count: string; // JSON string
  meeting_count_active: string; // JSON string
  meeting_count_text1: string;
  meeting_count_text2: string;
  meeting_count_text3: string;
  meeting_count_text4: string;
  meeting_count_total_text: string;

  // Patient type and clinic
  patient_type: string; // Adult, Kids, Soldier
  sub_clinic: string;
  obligation_verified: boolean;

  // Notes and tasks
  task: string; // Comments/notes
  tasks: PatientTask[];
  issues: string; // JSON string
  tasks_description: string;

  // Move/transfer fields
  move_clinic_status: string;
  move_clinic_id: number;
  move_clinic_comments: string;
  move_type?: string;

  // Payment
  payment_link?: string;
  // Both cardcom links, sent only for clinics that charge the patient (the private
  // clinics, and a patient being moved into one). Their presence is what gates the
  // row context menu's "Payment links" option.
  payment_intake_link?: string;
  payment_followup_link?: string;

  // Parent consent and verification
  parent_2_concent_link?: string;
  single_guardian_link?: string;

  [key: string]: any; // Allow other properties from API response
}

// Request payload for POST /patients_intake_paging
export interface GetPatientsIntakePayload {
  token: string;
  clinic_id: number;
  offset: number;
  pageSize: number;
  search: string;
  status: string;
  user_id: number;
  sub_clinic?: string;
  source?: string;
  sortedBy?: Array<{ sortBy: string; orderBy: string }>;
}

// API Response from POST /patients_intake_paging
export interface GetPatientsIntakeResponse {
  items: PatientIntakeItem[];
  total_count: number;
}

// ---- Top-bar patient search --------------------------------------------------

// The field the top-bar search matches on. Each maps to its own Xano endpoint
// (patients_search_name / _tz / _phone / _hm_id) behind GET /patients_search.
export type PatientSearchBy = "name" | "tz" | "phone" | "hm_id";

export interface SearchPatientsPayload {
  token: string;
  search_by: PatientSearchBy;
  value: string;
}

// Same shape as the patients table so results render with the same row code.
export interface SearchPatientsResponse {
  items: PatientIntakeItem[];
  total_count: number;
}

// ============================================================================
// Generic API Response Types
// ============================================================================

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// ============================================================================
// Pagination Types
// ============================================================================

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// ============================================================================
// Forgot Password Types
// ============================================================================

export interface ForgotPasswordRequest {
  email: string;
}

export interface ForgotPasswordResponse {
  message: string;
}

// ============================================================================
// Change Password Types
// ============================================================================

export interface ChangePasswordPayload {
  email: string;
  old_password: string;
  new_password: string;
}

export interface ChangePasswordResponse {
  status: 'ok' | 'fail';
  message: string;
}

export interface CheckForgotPasswordKeyPayload {
  key: string;
}

export interface CheckForgotPasswordKeyResponse {
  message: string;
}

export interface UpdateForgotPasswordPayload {
  key: string;
  newPassword: string;
}

export interface UpdateForgotPasswordResponse {
  message: string;
  status_code: number;
}


// ============================================================================
// Two-Factor Authentication Setup Types
// ============================================================================

export interface TwoFactorSetupRequiredError {
  detail: {
    message: string;
    email: string;
    google_secret: string;
  };
}

export interface Setup2FARequest {
  email: string;
}

export interface Setup2FAResponse {
  qr_code: string;
  secret: string;
  manual_entry_key: string;
  user_id: number;
  updated: boolean;
  error: string | null;
}

// ============================================================================
// Meetings Types
// ============================================================================

export interface Meeting {
  id: number;
  clinic_id: number;
  patient_id: number;
  user_id: number;
  meetingtype_id: number;
  start: number; // epoch milliseconds
  end: number; // epoch milliseconds
  comments: string;
  status: string;
  prep_status: string;
  re_intake: boolean;
  warning: string;
  team_url: string;
  yoman_id: string;
  yoman_url: string;
  HM_ID: string;
  patient_name: string;
  patient_phone: string;
  patient_type: string;
  case_manager_id: number;
  doctor_id: number;
  default_case_manager_id: number;
  default_doctor_id: number;
  meeting_url: string;
  source: string;
  color: string;
  red_flag?: boolean;
  is_test?: boolean;
  ad_hoc?: boolean;
}

export interface GetMeetingsByDatesPayload {
  token: string;
  meeting_type_id: number;
  clinic_id: number;
  user_id: number;
  only_my: boolean;
  from_date: number; // epoch milliseconds
  to_date: number; // epoch milliseconds
  // Lower-case meeting statuses to filter by (e.g. ["canceled", "noshow"]).
  // Each value is sent as its own `status=` query param. Omit or pass an empty
  // array to request all statuses.
  status?: string[];
}

export type MeetingsByDatesResponse = Meeting[];

export interface UpdateMeetingStatusPayload {
  token: string;
  meeting_id: number;
  status: string;
  source: string;
  meetingtype_id: number;
  reason_of_cancellation?: string;
  cancel_reason?: string;
  cancel_reason_additional_text?: string;
}

export interface UpdateMeetingStatusResponse {
  success: boolean;
  message?: string;
}

// ============================================================================
// Users Types
// ============================================================================

export interface GetUsersPayload {
  token: string;
  user_type: string;
  institution_id: number;
}

export interface ClinicUserApiResponse {
  id: number;
  created_at: number;
  updated_at: number | null;
  active: boolean;
  institution_id: number;
  name: string;
  email: string;
  phone?: string;
  heb_name?: string;
  type: string;
  role: string;
  clinic_admin: number;
  sub_clinic: string;
  sub_clinic_list: string[];
  clinicservices_id: number[];
  show_approve_treatment: boolean;
  show_prep_meeting: boolean;
  show_clicks_tasks: boolean;
  manage_users?: boolean;
  manage_clalit_excel?: boolean;
  // Additional optional fields from API response
  replacements_users?: any[];
  update_in_clicks_user_id?: number;
  password_set_on?: number | null;
  data?: string;
  update_summary_in_clicks?: boolean;
  phone_ext?: string;
  ext_id?: string;
  yoman_id?: string;
  last_login?: number | null;
  license?: string;
  doc_id?: string;
  interval?: number;
  lang?: string;
  folder_code?: string;
  last_patient_idx?: number;
  last_youth_idx?: number;
  synch_with_personal_calendar?: boolean;
  adult_folder?: string;
  youth_folder?: string;
  team_link?: string;
  sign_link?: string;
  default_clinic?: number;
  default_meeting_period?: string;
  tmp_key?: string;
  last_sent_key?: string | null;
  is_2FA?: boolean;
  is_2FA_setup?: boolean;
  secret_2FA?: string;
  google_secret?: string;
  hide_update_prescription?: boolean;
  ai_support?: boolean;
  ai_support_admin?: boolean;
}

export type GetUsersResponse = ClinicUserApiResponse[];

export interface CreateUserPayload {
  token: string;
  name: string;
  email: string;
  admin: string;
  data: string;
  type: string;
  heb_name: string;
  license: string;
  TZ: string;
  sign_link: string;
  yoman_id: string;
  phone: string;
  phone_ext: string;
  update_in_clicks_user_id: number;
  show_prep_meeting: boolean;
  update_summary_in_clicks: boolean;
  show_clicks_tasks: boolean;
  manage_users: boolean;
  manage_clalit_excel?: boolean;
}

export type CreateUserResponse = ClinicUserApiResponse;

export interface UpdateUserPayload {
  token: string;
  id: number;
  active?: boolean;
  name?: string;
  email?: string;
  admin?: string;
  data?: string;
  type?: string;
  heb_name?: string;
  license?: string;
  TZ?: string;
  sign_link?: string;
  yoman_id?: string;
  phone?: string;
  phone_ext?: string;
  update_in_clicks_user_id?: number;
  show_prep_meeting?: boolean;
  update_summary_in_clicks?: boolean;
  show_clicks_tasks?: boolean;
  manage_users?: boolean;
  manage_clalit_excel?: boolean;
}

export type UpdateUserResponse = ClinicUserApiResponse;

// ============================================================================
// Meeting Types
// ============================================================================

export interface MeetingType {
  id: number;
  created_at: number;
  updated_at: number | null;
  active: boolean;
  name: string;
  heb_name: string;
  default_length: number;
  user_type: string;
  yoman_id: string;
  yoman_id_dev: string;
  yoman_id_replacment: string;
  interval: number;
}

export type GetMeetingTypesResponse = MeetingType[];


// ============================================================================
// List View Types
// ============================================================================

// Data stored in the list view
export interface ListViewData {
  columnOrder: string[];
  columnWidths: Record<string, number>;
  columnVisibility: Record<string, boolean>;
  sortColumn: string;
  sortOrder: 'asc' | 'desc';
  description?: string;
  filters?: Record<string, any>;
  is_default?: boolean;
}

// API Response from GET /list_view/by_owner/{owner_id}/{list_name}
export interface ListView {
  id: number;
  name: string;
  owner_id: number;
  type: 'private' | 'public';
  list_name: string;
  data: ListViewData;
  created_at: string;
  updated_at: string;
}

// Request payload for POST /list_view
export interface CreateListViewPayload {
  token: string;
  name: string;
  owner_id: number;
  type: 'private' | 'public';
  list_name: string;
  data: ListViewData;
}

// Request payload for PUT /list_view/{id}
export interface UpdateListViewPayload {
  token: string;
  id: number;
  name?: string;
  type?: 'private' | 'public';
  data?: ListViewData;
}

// Request payload for DELETE /list_view/{id}
export interface DeleteListViewPayload {
  token: string;
}

export type GetListViewsResponse = ListView[];

// ============================================================================
// Patient Meetings Types
// ============================================================================

export interface PatientMeeting {
  id: number;
  date: number;
  start: number;
  end: number;
  meetingtype_id: number;
  user_id: number;
  status: string;
  team_url: string;
  medications: any[];
  diagnosis: string;
  diagnosis_text?: string;
  comments: string;
  summary_ready: boolean;
  summary_sent: boolean;
  summary_approved: boolean;
  is_doc: boolean;
  doc_file_id: string;
  need_followup_in_weeks: number;
  no_followups: boolean;
  yes_followups: boolean;
  skip_casemanger_followup: boolean;
  clinic_assignment: string;
  clinic_assignment_options: string[];
  allready_moved_clinic: boolean;
  warning: string;
  re_inatke: boolean;
  cancellation_time: number | null;
  reason_of_cancellation: string;
  cancel_reason?: string;
  cancel_reason_additional_text?: string;
  meeting_url: string;
  meeting_address: string;
  Severe: boolean;
  src: string;
  has_call?: boolean;
  ad_hoc?: boolean;
}

export interface GetPatientMeetingsPayload {
  token: string;
  patient_id: string | number;
}

export type GetPatientMeetingsResponse = PatientMeeting[];

// ============================================================================
// Meeting Summary Types
// ============================================================================

export interface Medication {
  id?: number;
  created_at?: number;
  updated_at?: number;
  created_by?: number;
  patient_id?: number;
  meeting_id?: number;
  medication_type_id?: number;
  dosage: string;                    // Decimal as string (e.g., "1.5")
  unit: string;                      // From MEDICATION_UNITS
  per_day?: string;
  how_many_days?: string;
  amount_per_take?: number;
  amount: number;                    // Decimal number
  amount_per: 'Day' | 'Week' | 'Month';
  duration: number;                  // Integer only
  number_of_packages?: number;
  likelihood_of_med_in_predictix?: string;
  start_date?: string | null;
  comments?: string;

  // Optional display fields
  name?: string;                     // For UI display
}

export interface MeetingSummary {
  start: number;
  meetingtype_id: number;
  name: string;
  patient_tz: string;
  doctor_copy_text: string;
  empty_transcript: boolean;
  clinic_id: number;
  is_maccabi: boolean;

  // Clinic-specific fields (clinic_id === 24)
  personal_demographics?: string;
  referral_reason?: string;
  background?: string;
  hpi?: string;

  // Suicidal questionnaire fields
  show_suicidal_questionnaire?: boolean;
  suicidal_quest_filled?: boolean;
  suicidal_quest_q1?: boolean;
  suicidal_quest_q2?: boolean;
  suicidal_quest_q3?: boolean;
  suicidal_quest_q4?: boolean;

  // Core fields
  summary_and_discussion_text: string;
  summary_and_discusion: string; // raw notes (note: typo in API)
  status_text: string;
  diagnosis: string; // Comma-separated string of diagnosis codes (e.g., "F43.1" or "F43.1,F41.2")
  diagnosis_text?: string;
  medications: Medication[];
  recommendations_text: string;
  recommandations: string; // raw notes (note: typo in API)
  summary_type?: 'UK_intake' | 'standard';
  prescription_updated?: boolean | string;
  summary_sent?: boolean | string;
}

export interface GetMeetingSummaryPayload {
  token: string;
  meeting_id: number;
}

export interface UpdateMeetingSummaryPayload {
  token: string;
  meeting_id: number;
  diagnosis: string;
  diagnosis_text?: string;
  medications: Medication[];
  recommandations: string;
  summary_and_discusion: string;
  status: string;
  clinic_id: number;
  demographics?: string;
  hpi?: string;
  background?: string;
  referral_reason?: string;
  send?: string;
  approved?: string;
  prescription_required?: string;
  update_prescription?: string;
}

export interface GetMeetingSummaryPDFPayload {
  token: string;
  meeting_id: number;
}

export interface PDFResponse {
  name: string;
  type: string;
  sizeBytes: number;
  base64Data: string;
}

// ============================================================================
// Diagnosis Types
// ============================================================================

export interface DiagnosisCode {
  code: string;
  Diagnosis: string;
}

export interface GetDiagnosisPayload {
  token: string;
}

export type GetDiagnosisResponse = DiagnosisCode[];

// ============================================================================
// Medication Type Types
// ============================================================================

export interface MedicationType {
  id: number;
  created_at: number;
  version: string;
  name: string;
  predictix: string;
  dosage_amount: number;
  dosage_unit: string;
  freq_amount: number;
  freq_period: string;
  amount_per_take: number;
  duration_in_days: number;
  times_amount: number;
  times_period: string;
  category: string;
  sub_category: string;
}

export interface GetMedicationTypesPayload {
  token: string;
}

export type GetMedicationTypesResponse = MedicationType[];

// ============================================================================
// Patient Files Types
// ============================================================================

export interface PatientFile {
  id: number;
  created_at: number;
  patient_id: number;
  user_id: number;
  description: string;
  updated_at: number;
  file_id: string;
  filename: string;
  patient_file_type_id: number;
  meeting_id: number;
  doc_file_id: string;
  sent_to_patient: boolean;
  sent_date: number | null;
  approved: boolean;
  approve_date: number | null;
  file_type: string;
}

export interface GetPatientFilesPayload {
  token: string;
  patient_id: string | number;
}

export type GetPatientFilesResponse = PatientFile[];

export interface PatientFileType {
  id: number;
  created_at: number;
  description: string;
  owner: string;
  field_name: string;
  heb_description: string;
}

export interface GetPatientFileTypesPayload {
  token: string;
  owner: string;
}

export type GetPatientFileTypesResponse = PatientFileType[];

export interface UpdatePatientFilePayload {
  token: string;
  description: string;
  file_id: number;
  patient_file_type_id: number;
}

export interface UpdatePatientFileResponse {
  success: boolean;
  message?: string;
}

export interface PatientUploadFilePayload {
  token: string;
  patient_id: number;
  filename: string;
  file_type: number;
  base64file: string;
  comments: string;
}

export interface PatientUploadFileResponse {
  success: boolean;
  message?: string;
}

export interface UploadFileType {
  id: number;
  description: string;
  file_upload_order: number;
}

export interface GetUploadFileTypesPayload {
  token: string;
  lang: string;
}

export type GetUploadFileTypesResponse = UploadFileType[];

export interface UploadFilesRequestPayload {
  token: string;
  patient_id: number;
  meeting_id: number;
  user_id: number;
  files_content: string[];
  files_ids: number[];
  files_message: string;
}

export interface UploadFilesRequestResponse {
  success: boolean;
  message?: string;
}



// ============================================================================
// CM Meeting Summary Types
// ============================================================================

export interface CMMeetingSummary {
  id: number;
  created_at: number;
  updated_at: number;
  active: boolean;
  institution_id: number;
  clinic_id: number;
  patient_id: number;
  patientservices_id: number;
  user_id: number;
  meetingtype_id: number;
  start: number;
  cycle: number;
  end: number;
  comments: string;
  finish: number;
  actual_length: number;
  status: string;
  prep_status: string;
  need_followup_in_weeks: number;
  yes_followups: boolean;
  no_followups: boolean;
  clinic_assignment: string;
  skip_casemanger_followup: boolean;
  re_intake: boolean;
  init: boolean;
  verify_ids: boolean;
  warning: string;
  transcript_get: boolean;
  no_transcript: boolean;
  created_by_synch: boolean;
  created_by: number;
  diagnosis: string;
  transcript: string;
  adtl_input: string;
  auto_summary: string;
  summary: string;
  final_summary: string;
  prescription: string;
  agenda: string;
  length: number;
  summary_saved: boolean;
  google_id: string;
  owner_email: string;
  participate_email: string;
  patient_questionnaire_id: number;
  team_id: string;
  team_url: string;
  bot_ids: any[];
  yoman_id: string;
  src: string;
  waiting_list: boolean;
  yoman_url: string;
  avail_slots: string;
  reason_of_cancellation: string;
  cancellation_time: number | null;
  empty_transcript: boolean;
  summary_ready: boolean;
  summary_approved: boolean;
  summary_sent: boolean;
  auto_summary_fix: boolean;
  meeting_city: string;
  meeting_street: string;
  meeting_street_num: string;
  meeting_street_other: string;
  name: string;
  patient_tz: string;
  HM_ID: string;
  summary_text: string;
}

export interface GetCMMeetingSummaryPayload {
  token: string;
  meeting_id: number;
}

export interface UpdateCMMeetingSummaryPayload {
  token: string;
  meeting_id: number;
  summary: string;
  approved?: string;
}

export interface UpdateCMMeetingPayload {
  token: string;
  id: number;
  patient_id: number;
  comments: string;
}

export interface UpdateCMMeetingResponse {
  output_message: string;
}

// ============================================================================
// PSY Meeting Insights Types
// ============================================================================

export interface PSYMeetingInsights {
  summary: string;
  recommendations: string;
  summary_and_discussion: string;
  medications?: Medication[];
  diagnosis?: string;
  diagnosis_text?: string;
}

export interface GetPSYMeetingInsightsPayload {
  token: string;
  meeting_id: number;
}

export interface UpdatePSYMeetingPayload {
  token: string;
  meeting_id: number;
  recommendations: string;
  summary_and_discussion: string;
  medications?: Medication[];
  diagnosis?: string;
  agenda?: string;
  need_followup_in_weeks?: number;
  yes_followups?: boolean;
  no_followups?: boolean;
  skip_casemanger_followup?: boolean;
  clinic_assignment?: string;
}

export interface UpdatePSYMeetingResponse {
  output_message: string;
}

export interface SavePSYMeetingPayload {
  token: string;
  id: number;
  medications: Medication[];
  comments: string;
  diagnosis: string;
  diagnosis_text: string;
  recommendations: string;
  summary_and_discussion: string;
  need_followup_in_weeks: number;
  skip_casemanger_followup: boolean;
  no_followups: boolean;
  clinic_assignment: string;
}

export interface SavePSYMeetingResponse {
  output_message: string;
}

// PSY Meeting Agenda Types
export interface PSYMeetingAgenda {
  message: string;
}

export interface GetPSYMeetingAgendaPayload {
  token: string;
  meeting_id: number;
}

export interface GetPSYMeetingAgendaResponse {
  message: string;
}

export interface FinishMeetingPayload {
  token: string;
  meeting_id: number;
  no_transcript: boolean;
  skip_warnings: boolean;
}

export interface FinishMeetingResponse {
  status: "OK" | "Error" | "Warn";
  section: string;
  text: string;
}

export interface RunTestPromptPayload {
  token: string;
  meeting_id: number;
  prompt_name: string;
  model_name: string;
  reasoning_effort?: string;
  test_type?: "summary" | "agenda";
}

export interface RunTestPromptResponse {
  status: string;
  message: string;
}

// ============================================================================
// Patient Page Types
// ============================================================================

export interface PatientPageIssue {
  issue: string;
  issue_severity: string;
  issue_type: string;
  issue_owner: number;
  issue_date: number;
  issue_meeting_id?: number;
  issue_meetingtype_id?: number;
}

export interface MeetingsSummaryData {
  id: number;
  created_at: number;
  meeting_id: number;
  patient_id: number;
  name: string;
  text: string;
}

export interface PatientPageData {
  id: number;
  patient_id: string;
  patient_name: string;
  first_name: string;
  last_name: string;
  HM_ID: string;
  status: string;
  is_test: boolean;
  // Whether to offer the patient schedule-page (IL yoman) link
  show_schedule_link?: boolean;
  age: string;
  DOB: string;
  sub_clinic: string;
  clinic_id: number;
  lang: string;
  doctor_id: number;
  // Resolved by the backend. The frontend's user list is filtered by institution, so a doctor
  // outside the caller's institution cannot be resolved client-side at all.
  doctor_name?: string;
  case_manager_id: number;
  default_case_manager_id: number;
  default_doctor_id: number;
  patient_type: string;
  questionnaire_id: number;
  task: string;
  next_meeting_start: number;
  next_meeting: number;
  next_meeting_team_url: string;
  next_meeting_yoman_url: string;
  next_meeting_type_id: number;
  email: string;
  phone: string;
  // The dialable number resolved by the backend (get_phone), as opposed to `phone` which is
  // formatted for display. Returned by patient_page but was never declared here.
  main_phone?: string;
  gender: string;
  HMO: string;
  children: number;
  parents_marital_status: string;
  parent_1_name: string;
  parent_1_gender: string;
  parent_1_age: number;
  parent_1_id: string;
  parent_1_email: string;
  parent_1_phone_number: string;
  parent_2_name: string;
  parent_2_gender: string;
  parent_2_age: number;
  parent_2_id: string;
  parent_2_email: string;
  parent_2_phone_number: string;
  overide_status: string;
  city: string;
  area: string;
  updated_at: number;
  cancel_reason: string;
  cancel_notes: string;
  obligation_intake: string;
  obligation_followup: string;
  obligation_verified: boolean;
  followup_obligation_verified: boolean;
  meeting_count: Record<string, number>;
  meeting_budget: Record<string, number>;
  "meeting-left": Record<string, number>;
  meeting_count_text1: string;
  meeting_count_text2: string;
  meeting_count_text3: string;
  meeting_count_text4: string;
  meeting_count_total_text: string;
  set_meeting_warnings: string[];
  // Youth-to-adult transfer. move_youth_to_adult_status is "" until the patient
  // is contacted; youth_transfer_link is "" unless the questionnaire is still
  // outstanding, so the copy button is shown iff it is non-empty.
  move_youth_to_adult_status: string;
  youth_transfer_link: string;
  reason_for_contact: string;
  current_place: string;
  address: string;
  show_suicidal_questionnaire: boolean;
  suicidal_quest_filled: boolean;
  suicidal_quest_q1: boolean;
  suicidal_quest_q2: boolean;
  suicidal_quest_q3: boolean;
  suicidal_quest_q4: boolean;
  Severe: boolean;
  contact_name: string;
  contact_phone: string;
  contact_role: string;
  issues: PatientPageIssue[];
  red_flag?: boolean;
  red_flag_reason?: string[];
  red_flag_other?: string;
  Diagnosis: string;
  Diagnosis_text?: string;
  last_meeting_date: number;
  medications: any[];
  meetings_summary: MeetingsSummaryData | null;
  upload_files_status?: string;
  upload_files_ids?: number[];
  upload_files_message?: string;
  // Medical opinions (מכתב לגורם חיצוני). Both are computed by the backend: the button is offered
  // to Maccabi patients with a done psychiatric intake (and, during the testing period,
  // is_test), and the red dot reflects a request still in pending_obligation/in_progress.
  show_medical_opinions?: boolean;
  has_open_medical_opinion?: boolean;
}

export interface GetPatientPagePayload {
  token: string;
  patient_id: string | number;
}

export type GetPatientPageResponse = PatientPageData;

export interface RedFlagRequestPayload {
  token: string;
  patient_id: number;
  user_id: number;
  red_flag_reason: string[];
  red_flag_other?: string;
  remove_flag?: boolean;
}

// ============================================================================
// Suicidal Questionnaire Types
// ============================================================================

export interface UpdatePatientSuicidalQuestionnairePayload {
  token: string;
  patient_id: number;
  user_id: number;
  q1: boolean;
  q2: boolean;
  q3: boolean;
  q4: boolean;
}

export type UpdatePatientSuicidalQuestionnaireResponse = PatientPageData;

// ============================================================================
// Update Finish Status Types
// ============================================================================

export interface UpdateFinishStatusPayload {
  token: string;
  patient_id: number;
  status: string;
  cancel_reason: string;
  notes: string;
}

export interface UpdateFinishStatusResponse {
  success: boolean;
  message?: string;
}

// ============================================================================
// Update Obligation Types
// ============================================================================

export interface UpdateObligationPayload {
  token: string;
  patient_id: number;
  obligation_intake: string;
  obligation_followup: string;
  intake_obligation_verified: boolean;
  followup_obligation_verified: boolean;
  not_maccabi: boolean;
}

export interface UpdateObligationResponse {
  success: boolean;
  message?: string;
  op?: 'keep' | 'delete';
}

// ============================================================================
// Move Patient Types
// ============================================================================

export interface MovePatientPayload {
  token: string;
  patient_id: number;
  move_clinic_status: string;
  move_clinic_comments: string;
}

export interface MovePatientResponse {
  op: 'keep' | 'delete';
}

// ============================================================================
// Patient Questionnaires Types
// ============================================================================

export interface PatientQuestionnaire {
  id: number;
  color: string;
  score: string;
  text: string;
  start_time: number;
  sent_time: number | null;
  type: string;
  patient_id: number;
}

export interface GetPatientQuestionnairesPayload {
  token: string;
  patient_id: string | number;
}

export type GetPatientQuestionnairesResponse = PatientQuestionnaire[];

// ============================================================================
// Questionnaire Details Types
// ============================================================================

export type QuestionnaireAnswer = Record<string, any>;

export interface GetQuestionnaireDetailsPayload {
  token: string;
  questionnaire_id: number;
}

export interface GetQuestionnaireDetailsResponse {
  questions: QuestionnaireAnswer[];
}

// ============================================================================
// Patient Suicidal Questionnaire History Types
// ============================================================================

export interface PatientSuicidalQuestHistory {
  id: number;
  created_at: number;
  patient_id: number;
  user_id: number;
  q1: boolean;
  q2: boolean;
  q3: boolean;
  q4: boolean;
  user_name: string;
}

export interface GetPatientSuicidalQuestHistoryPayload {
  token: string;
  patient_id: string | number;
}

export type GetPatientSuicidalQuestHistoryResponse = PatientSuicidalQuestHistory[];

// ============================================================================
// Upload Transcript Types
// ============================================================================

export interface UploadTranscriptPayload {
  token: string;
  meeting_id: number;
  filename: string;
  text: string;
}

export interface UploadTranscriptResponse {
  success: boolean;
  message?: string;
}

// ============================================================================
// Update Guardian Status Types
// ============================================================================

export interface UpdateGuardianStatusPayload {
  token: string;
  patient_id: number;
  single_guardian_verification_status: string;
  single_parent_reject_reason?: string;
  single_parent_reject_text?: string;
}

export interface UpdateGuardianStatusResponse {
  success?: boolean;
  message?: string;
  op: 'keep' | 'delete';
}

// ============================================================================
// Update Upload Files Contacted Types
// ============================================================================

export interface UpdateUploadFilesContactedPayload {
  token: string;
  patient_id: number;
}

export interface UpdateUploadFilesContactedResponse {
  success?: boolean;
  message?: string;
  op: 'keep' | 'delete';
}

// ── WhatsApp Messaging ──

export type WhatsAppMessageDirection = "inbound" | "outbound";
export type WhatsAppMessageStatus = "queued" | "sent" | "delivered" | "read" | "failed";

export interface WhatsAppMessage {
  id: number;
  phone_number: string;
  patient_id?: number;
  direction: WhatsAppMessageDirection;
  body: string;
  media_url?: string;
  file_name?: string;
  status: WhatsAppMessageStatus;
  sender_user_id?: number;
  sender_user_name?: string;
  created_at: string;
  updated_at?: string;
  is_template?: boolean;
}

export interface WhatsAppConversation {
  id: number;
  phone_number: string;
  patient_id?: number;
  patient_name?: string;
  patient_hm_id?: string;
  unread_count: number;
  last_message?: WhatsAppMessage;
  last_message_at?: string;
  last_message_preview?: string;
  can_send_freeform: boolean;
  assigned_user_id?: number;
  assigned_user_name?: string;
  status?: 'open' | 'solved' | 'pending' | 'campaign';
  wati_conversation_id?: string;
}

export interface GetWhatsAppMessagesPayload {
  token: string;
  phone_number: string;
  page?: number;
  limit?: number;
}

export interface GetWhatsAppMessagesResponse {
  messages: WhatsAppMessage[];
  total: number;
  page: number;
  has_more: boolean;
}

export interface SendWhatsAppMessagePayload {
  token: string;
  phone_number: string;
  conversation_id?: number;
  patient_id?: number;
  body?: string;
  media_url?: string;
  template_id?: string;
  template_variables?: Record<string, string>;
}

export interface SendWhatsAppMessageResponse {
  ok: boolean;
}

export interface GetWhatsAppConversationsPayload {
  token: string;
  page?: number;
  limit?: number;
  search?: string;
  status?: 'open' | 'closed' | 'pending';
}

export interface AssignWhatsAppConversationPayload {
  token: string;
  phone_number: string;
  user_id?: number;
  user_name?: string;
}

export interface UpdateWhatsAppConversationStatusPayload {
  token: string;
  phone_number: string;
  status: 'open' | 'solved' | 'pending';
}

export interface GetWhatsAppConversationsResponse {
  conversations: WhatsAppConversation[];
  total: number;
}

export interface MarkWhatsAppReadPayload {
  token: string;
  phone_number: string;
}

export interface WhatsAppTemplate {
  id: string;
  name: string;
  body: string;
  header?: string;
  language: string;
  variables?: Record<string, string>;         // body position → param_name
  header_variables?: Record<string, string>;  // header position → param_name
  param_defaults?: Record<string, string>;    // param_name → default value
}

export interface UploadClalitExcelPayload {
  token: string;
  filename: string;
  base64file: string;
}

export interface UploadClalitExcelResponse {
  success: boolean;
  message?: string;
  file_url?: string;
}

// Billing (Clalit & Maccabi monthly statements)
export interface RunBillingPayload {
  token: string;
  year: number;
  // 1-based month (e.g. 5 = May).
  month: number;
  // When true (only sent when running the current month while the previous
  // month is still unlocked), the backend also locks the previous month.
  lock_previous?: boolean;
}

export interface RunBillingResponse {
  status: string;
}

export interface LoadClalitRejectsPayload {
  token: string;
  filename: string;
  base64file: string;
  year: number;
  // 1-based month (e.g. 5 = May).
  month: number;
}

export interface ClalitRejectEntry {
  patient_id: string;
  sum: number;
}

export interface LoadClalitRejectsResponse {
  success: boolean;
  // "Incorrect Format" | "Rejects could not be found"
  error?: string;
  not_located?: ClalitRejectEntry[];
}

export interface AvailableBillingMonthsPayload {
  token: string;
  // "clalit" | "maccabi"
  institution: string;
}

export interface AvailableBillingMonth {
  year: number;
  // 1-based month (e.g. 5 = May).
  month: number;
}

// The backend returns only the months that are still open for billing:
// both previous and current month, or only the current month when the
// previous month is locked.
export type AvailableBillingMonthsResponse = AvailableBillingMonth[];

export interface WhatsAppSSEEvent {
  type: "new_message" | "status_update";
  message: WhatsAppMessage;
}

// ============================================================================
// Real-time Notifications (notification center / toast)
// ============================================================================

// A single user notification as returned by get_user_notifications. The backend
// is responsible for the fully-formatted (Hebrew, RTL) `message` text; the
// frontend renders it verbatim and only derives the relative timestamp.
// Event kind for a notification; drives which icon the frontend renders.
export type NotificationEvent =
  | "canceled_by_patient"
  | "new"
  | "canceled_by_staff"
  | "noshow";

export interface UserNotification {
  id: number;
  notification_datetime: number | string;
  patient_id: number;
  meeting_id: number;
  message: string;
  is_read: boolean;
  // Optional: when present and found inside `message`, the frontend renders the
  // name as an underlined link that opens the patient page.
  patient_name?: string;
  // Optional: drives the per-event icon shown in the toast/center.
  notification_event?: NotificationEvent;
}

export interface GetUserNotificationsPayload {
  token: string;
  user_id: number | string;
  notification_type: string;
}

export type GetUserNotificationsResponse = UserNotification[];

export interface MarkNotificationAsReadPayload {
  token: string;
  user_id: number | string;
  notification_id: number;
}

export interface RemoveNotificationsPayload {
  token: string;
  user_id: number | string;
}

// ============================================================================
// App (mobile patient-generated data)
// ============================================================================

// Mood options collected by the Daily Mood Check-In in the mobile app.
export type Mood = "Happy" | "Calm" | "Neutral" | "Sad" | "Anxious" | "Angry";

// A single daily mood check-in entry returned by get_patient_mood_status.
// rank: 1 for Sad/Anxious/Angry, 3 for Neutral, 5 for Happy/Calm.
export interface PatientMoodEntry {
  created_at: string;
  mood: Mood;
  rank: 1 | 3 | 5;
}

export interface GetPatientMoodStatusPayload {
  token: string;
  patient_id: number;
}

export type GetPatientMoodStatusResponse = PatientMoodEntry[];

// ============================================================================
// Voice (browser WhatsApp calling via Twilio Voice SDK)
// ============================================================================

export interface VoiceTokenResponse {
  token: string;
  identity: string;
  ttl: number;
}

export interface WhatsAppCallPermissionPayload {
  token: string;
  patient_id: number;
  phone?: string;
}

export interface WhatsAppCallPermissionResponse {
  success?: boolean;
  sid?: string;
  error?: string;
  // The patient already granted permanent call permission - nothing was sent.
  already_approved?: boolean;
  // Meta rate limit (error 63005): a request was already sent recently and is
  // still pending - wait for the patient to reply instead of resending.
  already_sent?: boolean;
}

// ============================================================================
// Medical Opinion (מכתב לגורם חיצוני) Types
// ============================================================================

// Label only - the workflow is identical for all six types.
export type MedicalOpinionType =
  | "idf"
  | "education"
  | "welfare"
  | "sal_shikum"
  | "bl_child"
  | "bl_adult";

export type MedicalOpinionStatus =
  | "pending_obligation"
  | "in_progress"
  | "completed"
  | "closed";

// input = uploaded by the initiator; output_summary = the signed pdf returned by the
// clinic; output_other = any other file the clinic returns.
export type MedicalOpinionFileType = "input" | "output_summary" | "output_other";

export interface MedicalOpinionFile {
  id: number;
  file_id: string;
  filename: string;
  content_type: string;
  file_size: number;
  file_type: MedicalOpinionFileType;
  created_at: number;
}

export interface MedicalOpinion {
  id: number;
  created_at: number;
  // 0 means the request was created by the patient in the patient application.
  created_by: number;
  patient_id: number;
  doctor_id: number;
  opinion_type: MedicalOpinionType;
  initiator_notes: string;
  status: MedicalOpinionStatus;
  obligation: number;
  obligation_date?: number;
  obligation_handler?: number;
  close_reason?: string;
  close_reason_notes?: string;
  finalized_at?: number;
  // Set by Create PDF and cleared by the unlock button, both in the letter modal. While it
  // is set the psychiatrist's fields are read-only and Update In Clicks is available; while
  // it is not, the reverse. It is what guarantees the stored letter still matches the copy
  // the psychiatrist uploaded to Clicks by hand -- nothing regenerates on completion. The
  // backend enforces both directions, so this drives the UI only.
  summary_locked?: boolean;
  files: MedicalOpinionFile[];
  // Resolved by the backend alongside the raw ids. The frontend cannot resolve these itself:
  // its user list comes from get_users(type="clinic"), which filters to the caller's
  // institution, so a doctor outside that set is absent from it entirely.
  doctor_name?: string;
  created_by_name?: string;
  obligation_handler_name?: string;
  // The Hebrew label for `status`, resolved by the backend. There is no list route for the
  // statuses the way there is for opinion types and close reasons: a status is set by the
  // workflow and never picked from a dropdown, so it travels with the record instead.
  heb_status?: string;
  // The Hebrew label for `close_reason`. Absent on a request that has not been closed. The
  // close reason list route still exists for the dropdown that picks one.
  heb_close_reason?: string;
}

export interface CreateMedicalOpinionFileInput {
  filename: string;
  base64file: string;
}

export interface CreateMedicalOpinionPayload {
  token: string;
  patient_id: number;
  opinion_type: MedicalOpinionType;
  // Mandatory - a request is not opened without an obligation number. It can still be
  // corrected later at the obligation approval step.
  obligation: number;
  initiator_notes?: string;
  files?: CreateMedicalOpinionFileInput[];
}

// Business-rule failures come back as {status: "error", message} with HTTP 200.
// Authorization failures are HTTP 403 instead, so callers must handle both shapes.
export interface CreateMedicalOpinionResponse {
  status: "success" | "error";
  message?: string;
}

export interface GetMedicalOpinionsPayload {
  token: string;
  patient_id: number;
}

export type GetMedicalOpinionsResponse = MedicalOpinion[];

// Single request by id, for callers that already know which one they want (the Psychiatric
// Opinion Request task links straight to it). A missing record comes back as 403, not 404 -
// the backend does not distinguish "gone" from "not yours", to avoid id enumeration.
export interface GetMedicalOpinionPayload {
  token: string;
  medical_opinion_id: number;
}

export type GetMedicalOpinionResponse = MedicalOpinion;

export type MedicalOpinionCloseReason =
  | "canceled"
  | "invalid"
  | "mistake"
  | "duplicate"
  | "other";

// The two dropdown lists are served by the backend (/medical_opinion_types,
// /medical_opinion_close_reasons) so the Hebrew labels have one source of truth and the web
// and patient applications cannot drift. The value unions stay here: they are the wire
// contract, not display text.
export interface MedicalOpinionTypeOption {
  value: MedicalOpinionType;
  label: string;
}

export interface MedicalOpinionCloseReasonOption {
  value: MedicalOpinionCloseReason;
  label: string;
  // Free text is mandatory for this reason. Carried by the backend so the client does not
  // hardcode which reason it is - the same rule is enforced server-side on submit.
  requires_notes: boolean;
}

export type GetMedicalOpinionTypesResponse = MedicalOpinionTypeOption[];

export type GetMedicalOpinionCloseReasonsResponse = MedicalOpinionCloseReasonOption[];

export interface GetMedicalOpinionFilePayload {
  token: string;
  patient_id: number;
  medical_opinion_id: number;
  file_id: string;
}

export interface GetMedicalOpinionFileResponse {
  filename?: string;
  content_type?: string;
  base64file?: string;
  // Present instead of the file when the object could not be read from S3.
  status?: "error";
  message?: string;
}

export interface MedicalOpinionObligationResponsePayload {
  token: string;
  patient_id: number;
  medical_opinion_id: number;
  // Editable at the approval step, so it is always sent - approving or closing.
  obligation: number;
  is_approved: boolean;
  // Both must be empty when approving. close_reason_notes is mandatory when the reason
  // is "other".
  close_reason?: MedicalOpinionCloseReason | "";
  close_reason_notes?: string;
}

// Business-rule failures arrive as {status:"error", message} with HTTP 200; authorization
// failures are HTTP 403. Callers must handle both.
export interface MedicalOpinionStatusResponse {
  status: "success" | "error";
  message?: string;
}

// ---------------------------------------------------------------------------
// The psychiatrist's letter (the Psychiatric Opinion Letter modal)
//
// Four routes: read the content, save it, generate the pdf, unlock. The lock is the
// mechanism that keeps the stored letter identical to the copy the psychiatrist uploads to
// Clicks by hand - see MedicalOpinion.summary_locked.
// ---------------------------------------------------------------------------

export interface GetMedicalOpinionPsyDataPayload {
  token: string;
  medical_opinion_id: number;
}

// The letter's content, plus what the modal needs to render around it. On the first open of
// a request the editable values are seeded from the patient's last psychiatric meeting
// (is_seeded true); after anything has been saved they come from the saved rows.
export interface MedicalOpinionPsyData {
  medical_opinion_id: number;
  patient_id: number;
  // The meeting the content was (or would be) seeded from. 0 when the patient has no
  // psychiatric meeting with a saved summary.
  source_meeting_id: number;
  is_seeded: boolean;
  // Mirrors MedicalOpinion.summary_locked, so opening the letter needs one call.
  summary_locked: boolean;
  // Always live from the last psychiatric meeting, never stored against the opinion, and
  // never editable here - the letter's medications are whatever was prescribed when the pdf
  // was generated.
  medications: Medication[];
  summary_and_discussion: string;
  status: string;
  recommendations: string;
  // Comma-separated icd codes and a JSON map of per-code notes, the same pair a meeting
  // carries - which is why the summary modal's diagnosis picker is reused verbatim.
  diagnosis: string;
  diagnosis_text: string;
}

// The editable content. Shared by save and pdf generation, so the letter that is rendered
// and the rows that are stored come from one and the same payload.
export interface MedicalOpinionPsyContentPayload {
  token: string;
  patient_id: number;
  medical_opinion_id: number;
  summary_and_discussion: string;
  status: string;
  recommendations: string;
  diagnosis: string;
  diagnosis_text: string;
}

export interface MedicalOpinionPsyResponsePayload extends MedicalOpinionPsyContentPayload {
  // false saves a draft, and is refused while the letter is locked. true is Update In
  // Clicks: it requires the lock, writes no content and does not regenerate the pdf - it
  // completes the request and notifies the patient.
  is_completed: boolean;
}

export interface MedicalOpinionUnlockPayload {
  token: string;
  patient_id: number;
  medical_opinion_id: number;
}

// Attach one file to a request that already exists. Creation uploads its files inside
// CreateMedicalOpinionPayload, because there is no opinion row yet; this is the route for
// afterwards. One file per call, so a file the backend rejects reports against itself
// instead of failing the whole selection. The type is always output_other and is not a
// parameter - input is the requester's, output_summary is the pdf route's alone.
export interface AddMedicalOpinionFilePayload {
  token: string;
  patient_id: number;
  medical_opinion_id: number;
  filename: string;
  base64file: string;
}

export interface AddMedicalOpinionFileResponse extends MedicalOpinionStatusResponse {
  file?: MedicalOpinionFile;
}

// Remove one output_other attachment. Only that type: the request's own files are the
// patient's, and the letter is managed by generating and reopening, never deleted by hand.
// The row and the stored object both go; patient_history keeps the record that it existed.
export interface DeleteMedicalOpinionFilePayload {
  token: string;
  patient_id: number;
  medical_opinion_id: number;
  file_id: string;
}

// Create PDF returns the stored letter's file row, so the caller can preview or download it
// through the ordinary file route without refetching the request.
export interface MedicalOpinionPdfResponse extends MedicalOpinionStatusResponse {
  file?: MedicalOpinionFile;
}

// ============================================================================
// AI Support
// ============================================================================

export type AISupportLangCode = "he" | "en";
export type AISupportOutcome =
  | "fixed"
  // Helped, but nothing was resolved — the therapist is done and does not
  // want the conversation left open.
  | "helped"
  | "didnt_help"
  | "need_human"
  // Set by the backend when a supporter closes a stale session; never sent
  // by the widget.
  | "closed_by_support";
export type AISupportStatus =
  | "active"
  | "working"
  | "awaiting_approval"
  | "escalated"
  | "closed";

/**
 * Identifiers and diagnostics from the browser — never records. The assistant
 * re-fetches anything it needs through the permission-checked endpoints.
 */
export interface AISupportClientContext {
  screen?: { type?: string; path?: string; params?: string; title?: string };
  entity?: { patient_id?: string | number; meeting_id?: string | number; task_id?: string | number };
  ui_state?: { modal?: string; tab?: string; filters?: string; selection?: string };
  console?: Array<{ level: string; message: string; at: string }>;
  requests?: Array<{
    method: string;
    url: string;
    status?: number;
    request_id?: string;
    detail?: string;
    at: string;
  }>;
  app?: { version?: string; browser?: string; os?: string; viewport?: string };
}

export interface AISupportStartPayload {
  token: string;
  lang: AISupportLangCode;
  support_console?: boolean;
  client_context?: AISupportClientContext;
}

export interface AISupportStartResponse {
  session_id: number;
  lang: AISupportLangCode;
  is_support_session: boolean;
}

export interface AISupportMessagePayload {
  token: string;
  session_id: number;
  message: string;
  client_context?: AISupportClientContext;
}

export interface AISupportFeedbackPayload {
  token: string;
  session_id: number;
  outcome: AISupportOutcome;
  note?: string;
}

export interface AISupportPendingAction {
  id: number;
  session_id: number;
  tool_use_id: string;
  action_name: string;
  arguments: Record<string, unknown> | null;
  status: string;
  requested_at: string | null;
  decided_by_user_id: number | null;
  decision_note: string | null;
  /**
   * Whether the person this was asked of may approve it themselves, rather than
   * waiting for human support. True for an action they already perform by hand
   * in the app -- resetting a colleague's two-factor, for somebody who
   * administers user accounts. The server decides it: the permission is on the
   * user record and the rule is on the action.
   */
  may_approve?: boolean;
}

/** One rule read out of the product's own source, with where it came from. */
export interface AISupportDerivedFact {
  key: string;
  value: unknown;
  unit: string | null;
  /** Why the value is what it is — the comment beside it in the source. */
  note: string | null;
  source: { repo: string; file: string; line: number; symbol: string };
}

export interface AISupportDerived {
  area: string;
  generated_at: string;
  source: { repo: string; file: string };
  facts: AISupportDerivedFact[];
}

/** A change distillation proposes to an entry that already exists. */
export interface AISupportKBRevision {
  id: number;
  entry_id: number;
  source_session_id: number | null;
  /** What the conversation showed that the entry does not say. */
  what_is_missing: string | null;
  /** Proposed text; null means "leave that field as it is". */
  symptom: string | null;
  resolution: string | null;
  status: string;
  created_at: string | null;
}

/** A fault in the product, and how many times it has been run into. */
export interface AISupportDefect {
  id: number;
  created_at: string | null;
  title: string;
  symptom: string | null;
  cause_code: string | null;
  status: "open" | "investigating" | "fixed" | "wont_fix" | (string & {});
  occurrence_count: number;
  first_seen_at: string | null;
  last_seen_at: string | null;
  external_ref: string | null;
  notes: string | null;
  resolved_at: string | null;
}

export interface AISupportSessionSummary {
  id: number;
  created_at: string | null;
  updated_at: string | null;
  user_id: number;
  user_name: string | null;
  clinic_id: number | null;
  lang: AISupportLangCode;
  is_support_session: boolean;
  /** Opened by a support admin — the team exercising the assistant. */
  is_test: boolean;
  title: string | null;
  status: AISupportStatus;
  outcome: AISupportOutcome | null;
  outcome_note: string | null;
  /** The assistant's own view. Always a proposal, never the record. */
  proposed_class: string | null;
  /** The decided class. Null while a proposal is still waiting on a supporter. */
  issue_class: string | null;
  class_decided_by_user_id: number | null;
  cause_code: string | null;
  cause_note: string | null;
  cause_by_assistant: boolean;
  /** Set when a supporter attached this session to a known defect. */
  defect_id: number | null;
  had_guidance: boolean;
  escalated_at: string | null;
  assigned_support_user_id: number | null;
  input_tokens: number;
  output_tokens: number;
  /**
   * When the conversation last had a message. Prefer this over `updated_at` for
   * display and ordering: `updated_at` moves on any metadata write, so a status
   * change would make an old conversation look active.
   */
  last_activity_at: string | null;
  /**
   * True while the therapist has the assistant panel open on this conversation.
   * Distinct from `status`: "open" means unresolved, "live" means someone is
   * looking at it right now.
   */
  is_live?: boolean;
  /** `working` but the turn has not touched the row for a while — likely dead. */
  is_stalled?: boolean;
}

/** One stored turn. `blocks` is the raw Anthropic content-block list. */
export interface AISupportStoredMessage {
  id: number;
  seq: number;
  /**
   * `guidance` is a supporter steering the assistant (never shown to the
   * therapist); `support_reply` is a supporter answering the therapist directly.
   */
  role: "user" | "assistant" | "system" | "guidance" | "support_reply";
  blocks: Array<Record<string, unknown>> | null;
  text: string | null;
  visible_to: "all" | "support_only";
  created_at: string | null;
}

export interface AISupportSessionDetail {
  session: AISupportSessionSummary;
  messages: AISupportStoredMessage[];
  pending_actions: AISupportPendingAction[];
  /**
   * The guide this conversation produced, recovered whole: steps re-pointed at
   * today's labels, and the screen it started on kept, so reopening it from
   * somewhere else can offer to take the therapist back there. Absent when the
   * conversation produced no guide. Shaped like `Walkthrough` in
   * AISupportWalkthrough.tsx; typed loosely here to keep types/api.ts free of
   * component imports.
   */
  walkthrough?: {
    title: string;
    steps: Array<Record<string, unknown>>;
    starts_at?: Record<string, unknown>;
  } | null;
}

/** A whitelisted action a supporter may run, or the assistant may propose. */
export interface AISupportAction {
  name: string;
  description: string;
  input_schema: {
    type: string;
    properties?: Record<string, { type?: string; description?: string }>;
    required?: string[];
  };
}

/** What an entry IS. It decides ranking: a reference page is the background to an
 *  answer, so it is ranked below a symptom entry when the question is a complaint. */
export type AISupportKBKind = "symptom" | "how-to" | "reference" | "policy";

/** What an entry is ABOUT. A closed list, unlike tags. */
export const AI_SUPPORT_KB_AREAS = [
  "summary", "transcript", "calls", "messaging", "scheduling", "calendar-sync",
  "tasks", "medical-opinion", "access", "patient-record", "policy", "reference",
] as const;
export type AISupportKBArea = (typeof AI_SUPPORT_KB_AREAS)[number];

export interface AISupportKBEntry {
  id: number;
  title: string;
  symptom: string | null;
  resolution: string | null;
  walkthrough: unknown[] | null;
  tags: string[] | null;
  lang: AISupportLangCode;
  kind: AISupportKBKind | null;
  area: AISupportKBArea | null;
  /** Sibling entries, by id — entries used to point at each other by quoted title. */
  related_ids?: number[] | null;
  /** Only `published` entries are ever shown to the assistant. */
  status: "draft" | "published" | "archived";
  source_type: "session_distilled" | "support_guidance" | "therapist_feedback" | "manual";
  source_session_id: number | null;
  use_count: number;
  /** Times the entry answered a problem the assistant failed to retrieve it for. */
  missed_count?: number;
  helpful_count: number;
  unhelpful_count: number;
  created_at: string | null;
  updated_at: string | null;
}

export interface AISupportKBResponse {
  entries: AISupportKBEntry[];
}

export interface AISupportSessionsResponse {
  sessions: AISupportSessionSummary[];
  stats: {
    total_rated: number;
    counts: Record<string, number>;
    resolution_rate: number | null;
  };
  /**
   * How many conversations each console filter would show, keyed by the filter
   * value ("" for All). Counted across every session rather than the filtered
   * page in `sessions`, which is why it comes from the server.
   */
  filter_counts?: Record<string, number>;
}
